/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Saturday, August 27th 2022, 6:52:57 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { SubscriptionPlan } from "saas-subscription/schemas";
import { BackdropLoader, OSDialog } from "ui-widgets";
import { useGlobalScope } from "core/providers";
import { ContinueSetupCTA } from "onboarding/components/ContinueSetupCTA";
import { PurchaseSuccessModal } from "saas-subscription/components/PurchaseSuccessModal";
import { getPurchaseFlowStrategyHook } from "saas-subscription/strategy";
import { getPriceFromInterval } from "saas-subscription/utils";
import { PurchaseForm } from "./PurchaseForm";

interface SubscriptionPurchaseProps {
  subscriptionPlan: SubscriptionPlan;
  priceInterval: SubscriptionPlan["prices"][number]["recurring"]["interval"];
  strategy: ReturnType<ReturnType<typeof getPurchaseFlowStrategyHook>>;
}

export const SubscriptionPurchase = (props: SubscriptionPurchaseProps) => {
  const {
    selectedOrganization,
    isSuccessful,
    isError,
    onConfirmPurchase,
    isMakingPurchase,
    purchaseFormIcon,
    purchaseFormTitle,
    purchaseFormBody,
    purchaseSuccessTitle,
    purchaseSuccessContent,
  } = useSubscriptionPurchaseLogic(props);

  return (
    <>
      <BackdropLoader open={isMakingPurchase} />

      <OSDialog open={isError}>
        <ContinueSetupCTA organizationName={selectedOrganization?.name} />
      </OSDialog>

      <OSDialog open={isSuccessful}>
        <PurchaseSuccessModal
          organization={selectedOrganization}
          subscriptionPlan={props.subscriptionPlan}
          title={purchaseSuccessTitle}
          content={purchaseSuccessContent}
        />
      </OSDialog>

      <PurchaseForm
        strategy={props.strategy}
        subscriptionPlan={props.subscriptionPlan}
        onConfirmPurchase={onConfirmPurchase}
        purchaseFormTitle={
          <Stack direction="row" spacing={2}>
            {purchaseFormIcon}
            <Typography variant="h5" color="gray">
              {purchaseFormTitle}
            </Typography>
          </Stack>
        }
        purchaseFormBody={purchaseFormBody}
      />
    </>
  );
};

const useSubscriptionPurchaseLogic = ({ subscriptionPlan, priceInterval, strategy }: SubscriptionPurchaseProps) => {
  const {
    globalState: { selectedOrganization },
  } = useGlobalScope();
  const { packageColor } = strategy.getWidgets(subscriptionPlan);
  const { useHandlePurchase } = strategy;
  const { handlePurchase, isError, isMakingPurchase, isSuccessful, purchaseSuccessContent, purchaseSuccessTitle } =
    useHandlePurchase({
      selectedOrganization,
      subscriptionPlan,
    });
  const selectedPrice = getPriceFromInterval({ subscriptionPlan, priceInterval });
  const purchaseFormTitle = strategy.getPurchaseFormTitle(subscriptionPlan);
  const purchaseFormIcon = strategy.getPurchaseFormIcon(subscriptionPlan);
  const purchaseFormBody = strategy.getPurchaseFormBody({
    subscriptionPlan,
    selectedOrganization,
    packageColor,
    selectedPrice,
  });
  const onConfirmPurchase = () => handlePurchase({ selectedPrice });

  return {
    selectedOrganization,
    packageColor,
    isMakingPurchase,
    isSuccessful,
    isError,
    onConfirmPurchase,
    purchaseFormTitle,
    purchaseFormIcon,
    purchaseFormBody,
    purchaseSuccessTitle,
    purchaseSuccessContent,
  };
};
